import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { statusClass, statusLabel } from '../utils/statusBadge';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { ButtonGroup } from '@/components/ui/button-group';
import { MagnifyingGlassIcon, PlusIcon, ClipboardDocumentListIcon, CheckIcon, XMarkIcon, TruckIcon } from '@heroicons/react/24/outline';
import { getAppSocket } from '@/lib/wesSocket';
import { Tip } from '@/components/ui/tip';

type SupplyRequest = {
  id: string;
  supplyId: string;
  quantity: number;
  status: 'pending' | 'approved' | 'rejected' | 'delivered';
  requestedBy?: string;
  approvedBy?: string;
  approvedAt?: string;
  deliveredAt?: string;
  urgency?: string;
  reason?: string;
  serviceName?: string;
  costCenter?: string;
  createdAt: string;
};

type SupplyOption = {
  id: string;
  name: string;
};

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'delivered', label: 'Entregados' },
  { value: 'rejected', label: 'Rechazados' }
] as const;

const SupplyCombobox: React.FC<{
  supplies: SupplyOption[];
  value: string;
  onChange: (id: string) => void;
}> = ({ supplies, value, onChange }) => {
  const [query, setQuery] = useState('');
  const selected = supplies.find((s) => s.id === value) || null;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return supplies;
    return supplies.filter((s) => (s.name || '').toLowerCase().includes(q));
  }, [supplies, query]);

  return (
    <Combobox value={selected} onChange={(s: SupplyOption | null) => onChange(s?.id || '')} by="id">
      <div className="relative">
        <div className="relative">
          <Combobox.Input
            className="w-full h-9 px-3 pl-9 pr-9 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-sm"
            placeholder="Buscar insumo..."
            displayValue={(s: SupplyOption | null) => s?.name || ''}
            onChange={(event) => setQuery(event.target.value)}
          />
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Combobox.Button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg shadow-lg border bg-card">
          {filtered.length === 0 && query.trim() !== '' && (
            <div className="px-4 py-4 text-center text-muted-foreground">
              <p className="text-sm font-medium">No se encontraron insumos</p>
            </div>
          )}
          {filtered.map((s) => (
            <Combobox.Option key={s.id} value={s} as={Fragment}>
              {({ active, selected }) => (
                <li
                  className={`relative cursor-pointer select-none px-4 py-2 transition-colors ${
                    active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${selected ? 'font-semibold' : 'font-normal'}`}>{s.name}</span>
                    {selected && <CheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" />}
                  </div>
                </li>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </div>
    </Combobox>
  );
};

const SupplyRequestsPage: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const [requests, setRequests] = useState<SupplyRequest[]>([]);
  const [supplies, setSupplies] = useState<SupplyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<typeof STATUS_FILTERS[number]['value']>('all');
  const [search, setSearch] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState<{ supplyId: string; quantity: number; urgency?: string; reasonType?: string; reasonDetail?: string; costCenter?: string; serviceName?: string }>({
    supplyId: '',
    quantity: 1,
    costCenter: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const canManage = hasPermission('supplies');
  const canRequest = hasPermission('request_supplies');
  const canApprove = hasPermission('approve_supply_requests');
  const canView = canManage || canApprove || canRequest;

  const fetchRequests = async (options?: { silent?: boolean }) => {
    if (!canView) return;
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const params: Record<string, any> = { page: 1, limit: 100 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get('/api/supplies/requests/all', { params });
      setRequests(res.data.requests || []);
    } catch (error: any) {
      console.error('Error fetching requests', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar los pedidos');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchSupplies = async () => {
    try {
      const res = await axios.get('/api/supplies', { params: { page: 1, limit: 200 } });
      setSupplies((res.data.supplies || []).map((s: any) => ({ id: s.id, name: s.name })));
    } catch (error: any) {
      console.error('Error fetching supplies list', error);
    }
  };

  useEffect(() => {
    fetchSupplies();
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, canView]);

  useEffect(() => {
    if (!canView) return;
    const socket = getAppSocket();
    let refreshTimer: number | null = null;

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/supplies') || path.startsWith('/api/purchases') || path.startsWith('/api/service-reports'))) {
        return;
      }

      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        fetchRequests({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [canView, statusFilter]);

  const createRequest = async () => {
    try {
      if (!requestForm.supplyId || requestForm.quantity <= 0) {
        toast.error('Selecciona insumo y cantidad');
        return;
      }
      const reason =
        requestForm.reasonType === 'otro'
          ? `Otro: ${requestForm.reasonDetail || ''}`.trim()
          : requestForm.reasonType || '';
      setSubmitting(true);
      await axios.post('/api/supplies/requests', { ...requestForm, reason });
      toast.success('Pedido enviado');
      setShowRequestModal(false);
      setRequestForm({ supplyId: '', quantity: 1, costCenter: getUserCostCenter() });
      fetchRequests();
    } catch (error: any) {
      console.error('Error creating request', error);
      toast.error(error.response?.data?.error || 'No se pudo crear el pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestAction = async (id: string, action: 'approve' | 'reject' | 'deliver') => {
    try {
      await axios.post(`/api/supplies/requests/${id}/${action}`);
      const label = action === 'approve' ? 'aprobado' : action === 'reject' ? 'rechazado' : 'entregado';
      toast.success(`Pedido ${label}`);
      fetchRequests();
    } catch (error: any) {
      console.error('Error updating request', error);
      toast.error(error.response?.data?.error || 'No se pudo actualizar el pedido');
    }
  };

  const getSupplyName = useCallback((id: string) => supplies.find((s) => s.id === id)?.name || 'Insumo', [supplies]);

  const getUserCostCenter = useCallback(() => {
    if (!user) return '';
    return user.role || user.username || '';
  }, [user]);

  useEffect(() => {
    setRequestForm((prev) => ({
      ...prev,
      costCenter: getUserCostCenter()
    }));
  }, [getUserCostCenter]);

  const filteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return requests;
    return requests.filter((request) => {
      const supplyName = getSupplyName(request.supplyId).toLowerCase();
      const reason = request.reason?.toLowerCase() || '';
      const center = request.costCenter?.toLowerCase() || '';
      const requester = request.requestedBy?.toLowerCase() || '';
      const approver = request.approvedBy?.toLowerCase() || '';
      return (
        supplyName.includes(term) ||
        reason.includes(term) ||
        center.includes(term) ||
        requester.includes(term) ||
        approver.includes(term)
      );
    });
  }, [requests, search, getSupplyName]);

  const formatStatusBadge = (status: SupplyRequest['status']) => (
    <Badge className={statusClass('supplyRequest', status)}>{statusLabel('supplyRequest', status)}</Badge>
  );

  const getAgeBadge = (createdAt?: string) => {
    if (!createdAt) return <Badge variant="secondary">-</Badge>;
    const created = new Date(createdAt);
    const days = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
    if (days >= 7) return <Badge className="bg-red-100 text-red-700">+ 7 días</Badge>;
    if (days >= 2) return <Badge className="bg-amber-100 text-amber-800">+ 2 días</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-800">Hoy</Badge>;
  };

  if (!canView) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        No tienes permisos para ver los pedidos internos.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Pedidos internos</h1>
            <p className="text-sm text-muted-foreground">Gestiona las solicitudes de insumos del equipo.</p>
          </div>
          {canRequest && (
            <Button
              onClick={() => setShowRequestModal(true)}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium h-9 rounded-lg gap-1.5 px-4 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
            >
              <PlusIcon className="h-4 w-4" />
              Nuevo pedido
            </Button>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-80">
            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
            <Input
              placeholder="Buscar por insumo, motivo o área..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-w-0 rounded-lg border bg-white px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-9 pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm text-muted-foreground">Estado</div>
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
              <SelectTrigger className="w-full md:w-52">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card className="mt-6">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : !filteredRequests.length ? (
              <div className="text-center py-12">
                <ClipboardDocumentListIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {search ? 'No hay pedidos internos que coincidan con la búsqueda' : 'No hay pedidos internos para mostrar'}
                </p>
              </div>
            ) : (
              <>
                <div className="md:hidden space-y-3 p-4">
                  {filteredRequests.map((request) => (
                    <div key={request.id} className="rounded-xl border bg-card/80 p-4 shadow-sm space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Insumo</p>
                          <p className="text-base font-semibold text-foreground">{getSupplyName(request.supplyId)}</p>
                          {request.serviceName && (
                            <p className="text-sm text-muted-foreground">{request.serviceName}</p>
                          )}
                        </div>
                        {formatStatusBadge(request.status)}
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Cantidad</p>
                          <p className="font-medium text-foreground">{request.quantity}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Urgencia</p>
                          <p className="font-medium text-foreground">
                            {request.urgency ? request.urgency.charAt(0).toUpperCase() + request.urgency.slice(1) : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Motivo</p>
                          <p className="font-medium text-foreground">{request.reason || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Antiguedad</p>
                          <div>{getAgeBadge(request.createdAt)}</div>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Solicito</p>
                          <p className="font-medium text-foreground">{request.requestedBy || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Aprobo</p>
                          <p className="font-medium text-foreground">{request.approvedBy || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground">Area</p>
                          <p className="font-medium text-foreground">{request.costCenter || '-'}</p>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Creado: {request.createdAt ? new Date(request.createdAt).toLocaleString('es-AR') : '-'}
                      </div>
                      {canApprove && (
                        <div className="flex flex-wrap gap-2">
                          {request.status === 'pending' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 min-w-[110px] justify-center"
                                onClick={() => handleRequestAction(request.id, 'approve')}
                              >
                                <CheckIcon className="h-4 w-4 mr-1" />
                                Aprobar
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="flex-1 min-w-[110px] justify-center"
                                onClick={() => handleRequestAction(request.id, 'reject')}
                              >
                                <XMarkIcon className="h-4 w-4 mr-1" />
                                Rechazar
                              </Button>
                            </>
                          )}
                          {request.status === 'approved' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-[110px] justify-center"
                              onClick={() => handleRequestAction(request.id, 'deliver')}
                            >
                              <TruckIcon className="h-4 w-4 mr-1" />
                              Entregar
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="relative w-full overflow-x-auto hidden md:block">
                  <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Insumo</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Cantidad</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Motivo</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Urgencia</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Solicitó</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Aprobó</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Antigüedad</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Área</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Creado</TableHead>
  <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right">Acciones</TableHead>
</TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRequests.map((request) => (
                      <TableRow key={request.id} className="border-b hover:bg-muted/50">
  <TableCell className="p-2 align-middle">
    <div className="font-medium">{getSupplyName(request.supplyId)}</div>
    {request.serviceName && (
      <div className="text-xs text-muted-foreground">{request.serviceName}</div>
    )}
  </TableCell>
  <TableCell className="p-2 align-middle">{request.quantity}</TableCell>
  <TableCell className="p-2 align-middle">{request.reason || '-'}</TableCell>
  <TableCell className="p-2 align-middle">{formatStatusBadge(request.status)}</TableCell>
  <TableCell className="p-2 align-middle">
    {request.urgency ? request.urgency.charAt(0).toUpperCase() + request.urgency.slice(1) : '-'}
  </TableCell>
  <TableCell className="p-2 align-middle">{request.requestedBy || '-'}</TableCell>
  <TableCell className="p-2 align-middle">{request.approvedBy || '-'}</TableCell>
  <TableCell className="p-2 align-middle">{getAgeBadge(request.createdAt)}</TableCell>
  <TableCell className="p-2 align-middle">{request.costCenter || '-'}</TableCell>
  <TableCell className="p-2 align-middle">
    {request.createdAt ? new Date(request.createdAt).toLocaleString('es-AR') : '-'}
  </TableCell>
  <TableCell className="p-2 align-middle text-right" onClick={(e) => e.stopPropagation()}>
    <div className="flex items-center justify-end">
      <ButtonGroup>
        {canApprove && request.status === 'pending' && (
          <>
            <Tip label="Aprobar">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestAction(request.id, 'approve');
                }}
              >
                <CheckIcon className="h-4 w-4" />
              </Button>
            </Tip>
            <Tip label="Rechazar">
              <Button
                variant="destructive"
                size="icon"
                className="size-8"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestAction(request.id, 'reject');
                }}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            </Tip>
          </>
        )}
        {canApprove && request.status === 'approved' && (
          <Tip label="Registrar entrega">
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestAction(request.id, 'deliver');
              }}
            >
              <TruckIcon className="h-4 w-4" />
            </Button>
          </Tip>
        )}
      </ButtonGroup>
    </div>
  </TableCell>
</TableRow>
                    ))}
                  </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo pedido interno</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div className="space-y-1">
              <Label>Insumo</Label>
              <SupplyCombobox
                supplies={supplies}
                value={requestForm.supplyId}
                onChange={(id) => setRequestForm((prev) => ({ ...prev, supplyId: id }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Cantidad</Label>
              <NumberInput
                min={1}
                value={requestForm.quantity}
                onChange={(v) => setRequestForm((prev) => ({ ...prev, quantity: v }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Urgencia</Label>
              <Select
                value={requestForm.urgency || ''}
                onValueChange={(value) => setRequestForm((prev) => ({ ...prev, urgency: value }))}
              >
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja">Baja</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Motivo</Label>
              <Select
                value={requestForm.reasonType || ''}
                onValueChange={(value) => setRequestForm((prev) => ({ ...prev, reasonType: value }))}
              >
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rotura">Rotura</SelectItem>
                  <SelectItem value="reposicion">Reposición</SelectItem>
                  <SelectItem value="buscar opciones">Buscar otras opciones</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {requestForm.reasonType === 'otro' && (
              <div className="space-y-1 md:col-span-2">
                <Label>Detalle</Label>
                <Input
                  value={requestForm.reasonDetail || ''}
                  onChange={(e) => setRequestForm((prev) => ({ ...prev, reasonDetail: e.target.value }))}
                  placeholder="Detalle breve"
                />
              </div>
            )}
            <div className="space-y-1 md:col-span-2">
              <Label>Servicio/Trabajo (opcional)</Label>
              <Input
                value={requestForm.serviceName || ''}
                onChange={(e) => setRequestForm((prev) => ({ ...prev, serviceName: e.target.value }))}
                placeholder="Ej: Alineación, Cambio de aceite"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestModal(false)}>Cancelar</Button>
            <Button onClick={createRequest} disabled={submitting}>
              {submitting ? 'Enviando...' : 'Crear pedido'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplyRequestsPage;
